import React from 'react';
import { Alert, Box, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import CAReviewFilterBar from './CAReviewFilterBar';
import CAPackageGrid from './CAPackageGrid';
import CADecisionGrid from './CADecisionGrid';
import { createCaPackage, fetchCaDecisions, fetchCaPackageCards, fetchCaPackages, recordCaDecision, submitCaPackage } from './caReviewApi';
import BudgetVersionCardsPanel from '../budgetversion/BudgetVersionCardsPanel';

type State = {
  fiscalYear:string; status:string; budgetVersionId:number|''; ppmId:number|''; packageTitle:string; meetingDate:string; meetingReference:string; notes:string;
  decisionType:string; decisionScope:string; decisionDate:string; observations:string; remarks:string;
  packages:any[]; selected:any|null; cards:any; decisions:any[]; loading:boolean; error:string; success:string;
};

class CAReviewWorkbench extends React.Component<any, State> {
  state: State = { fiscalYear:'2026', status:'', budgetVersionId:'', ppmId:'', packageTitle:'Budget and PPM Review Package', meetingDate:'', meetingReference:'', notes:'', decisionType:'APPROVED', decisionScope:'BOTH', decisionDate:'', observations:'', remarks:'', packages:[], selected:null, cards:{}, decisions:[], loading:false, error:'', success:'' };

  componentDidMount(){ this.loadPackages(); }

  setAny = (name:string, value:any) => this.setState({[name]: value} as any);

  loadPackages = async () => {
    this.setState({loading:true,error:'',success:''});
    try {
      const packages = await fetchCaPackages(this.state.status, this.state.fiscalYear);
      const selected = packages.length ? packages[0] : null;
      this.setState({packages, selected});
      if (selected) await this.loadPackage(selected);
    } catch(err:any){ this.setState({error:err.message||String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  loadPackage = async (selected:any) => {
    this.setState({selected,loading:true,error:'',success:''});
    try {
      const [cards, decisions] = await Promise.all([fetchCaPackageCards(selected.rid), fetchCaDecisions(selected.rid)]);
      this.setState({cards, decisions});
    } catch(err:any){ this.setState({error:err.message||String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  createPackage = async () => {
    if(!this.state.budgetVersionId){this.setState({error:'Budget Version ID is required.'}); return;}
    this.setState({loading:true,error:'',success:''});
    try {
      await createCaPackage({ bvi:this.state.budgetVersionId, ppi:this.state.ppmId || null, nam:this.state.packageTitle, mdt:this.state.meetingDate || null, mrf:this.state.meetingReference, nts:this.state.notes });
      this.setState({success:'CA package created.'});
      await this.loadPackages();
    } catch(err:any){ this.setState({error:err.message||String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  submitPackage = async () => {
    if(!this.state.selected) return;
    this.setState({loading:true,error:'',success:''});
    try { await submitCaPackage(this.state.selected.rid, this.state.notes); this.setState({success:'CA package submitted.'}); await this.loadPackages(); }
    catch(err:any){ this.setState({error:err.message||String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  saveDecision = async () => {
    if(!this.state.selected) return;
    this.setState({loading:true,error:'',success:''});
    try {
      await recordCaDecision({ rid:this.state.selected.rid, dty:this.state.decisionType, dsc:this.state.decisionScope, ddt:this.state.decisionDate || null, mrf:this.state.meetingReference, obs:this.state.observations, rmk:this.state.remarks });
      this.setState({success:'CA decision recorded.'});
      await this.loadPackage(this.state.selected);
    } catch(err:any){ this.setState({error:err.message||String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  renderDecisionForm(){
    const s=this.state;
    return <Paper elevation={1} sx={{p:2}}>
      <Typography variant="h6" sx={{mb:2}}>Decision Entry</Typography>
      <Grid container spacing={2}>
        <Grid size={{xs:12,md:2}}><TextField select size="small" label="Decision" value={s.decisionType} onChange={(e)=>this.setState({decisionType:e.target.value})} fullWidth><MenuItem value="APPROVED">Approved</MenuItem><MenuItem value="REJECTED">Rejected</MenuItem><MenuItem value="CONDITIONAL">Conditional</MenuItem></TextField></Grid>
        <Grid size={{xs:12,md:2}}><TextField select size="small" label="Scope" value={s.decisionScope} onChange={(e)=>this.setState({decisionScope:e.target.value})} fullWidth><MenuItem value="BUDGET">Budget</MenuItem><MenuItem value="PPM">PPM</MenuItem><MenuItem value="BOTH">Both</MenuItem></TextField></Grid>
        <Grid size={{xs:12,md:2}}><TextField type="date" size="small" label="Decision Date" value={s.decisionDate} onChange={(e)=>this.setState({decisionDate:e.target.value})} fullWidth InputLabelProps={{shrink:true}} /></Grid>
        <Grid size={{xs:12,md:3}}><TextField size="small" label="Observations" value={s.observations} onChange={(e)=>this.setState({observations:e.target.value})} fullWidth /></Grid>
        <Grid size={{xs:12,md:3}}><TextField size="small" label="Remarks" value={s.remarks} onChange={(e)=>this.setState({remarks:e.target.value})} fullWidth /></Grid>
      </Grid>
    </Paper>;
  }

  render(){
    const s=this.state;
    return <Box sx={{p:3}}>
      <Stack spacing={3}>
        <Box><Typography variant="h4" sx={{fontWeight:700}}>CA Review & Decision Register</Typography><Typography variant="body2" color="text.secondary">Governance packaging and external CA decision tracking.</Typography></Box>
        <CAReviewFilterBar fiscalYear={s.fiscalYear} status={s.status} budgetVersionId={s.budgetVersionId} ppmId={s.ppmId} packageTitle={s.packageTitle} meetingDate={s.meetingDate} meetingReference={s.meetingReference} notes={s.notes} selected={s.selected} loading={s.loading} onChange={this.setAny} onRefresh={this.loadPackages} onCreatePackage={this.createPackage} onSubmitPackage={this.submitPackage} onRecordDecision={this.saveDecision}/>
        {s.error && <Alert severity="error">{s.error}</Alert>}
        {s.success && <Alert severity="success">{s.success}</Alert>}
        <BudgetVersionCardsPanel cards={s.cards}/>
        <Grid container spacing={3}>
          <Grid size={{xs:12}}><CAPackageGrid rows={s.packages} selectedId={s.selected?.rid} loading={s.loading} onSelect={this.loadPackage}/></Grid>
          <Grid size={{xs:12}}>{this.renderDecisionForm()}</Grid>
          <Grid size={{xs:12}}><CADecisionGrid rows={s.decisions} loading={s.loading}/></Grid>
        </Grid>
      </Stack>
    </Box>;
  }
}
export default CAReviewWorkbench;
